/**
 * One undo step for a whole run of stepping a number field.
 *
 * Stepping — spin arrows, ↑ / ↓, the mouse wheel — commits every step at once so it shows on
 * the canvas (see useNumberStep). Each commit would be an undo step of its own, and wheeling a
 * value from 10 to 30 took twenty undos to get back. While a field is being stepped its
 * commits share a group id, which `redux-undo`'s `groupBy` (undoableReduxer) folds into one
 * history entry — one undo puts the value back where the stepping started, as it did when
 * only blur / Enter committed.
 *
 * A run ends on a pause, on stepping another field, or on a typed commit (blur / Enter), which
 * stays a step of its own.
 */

/** No step for this long and the next one starts a new undo step. */
const STEP_RUN_PAUSE_MS = 700

let runCount = 0
let activeGroup: string | null = null
let activeField: EventTarget | null = null
let pauseTimer: number | undefined

/** The current run's group, or null — what `groupBy` folds commits by. */
export const currentStepGroup = (): string | null => activeGroup

/** End the current run: the next commit is an undo step of its own. */
export const endStepRun = () => {
    window.clearTimeout(pauseTimer)
    activeGroup = null
    activeField = null
}

/**
 * Call right before committing a STEP of `field` (its input element), so the commit joins the
 * field's current run — or starts one.
 */
export const markStep = (field: EventTarget | null) => {
    if (!activeGroup || field !== activeField) {
        runCount += 1
        activeGroup = `number-step-${runCount}`
        activeField = field
    }
    window.clearTimeout(pauseTimer)
    pauseTimer = window.setTimeout(endStepRun, STEP_RUN_PAUSE_MS)
}
